import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { isEqual } from 'vet-bones/utils';
import {
  usePortalHeap,
  usePortalRollbar,
  usePortalUserflow,
} from 'vet-bones/utils/portal';

import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import { ROUTE } from 'src/constants/routes';
import { connectionAsyncActions } from 'src/store/connections/connections.slice';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';

/**
 * Define the app-level hooks as a child of the App to prevent
 * the App (and all of its children) from unnecessarily re-rendering
 * every time a dependency of the app-level hooks changes.
 */
export const AppHooks: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const location = useLocation();
  const didLocationChange = useDidValueChange(location, isEqual);
  const defaultPortalConnection = useDefaultPortalConnection();
  const didDefaultConnectionChange = useDidValueChange(
    defaultPortalConnection,
    isEqual
  );
  usePortalHeap();
  usePortalUserflow();
  usePortalRollbar();

  // always refresh default portal connection data
  React.useEffect(() => {
    if (
      didDefaultConnectionChange &&
      defaultPortalConnection &&
      (defaultPortalConnection.token ||
        defaultPortalConnection.password ||
        defaultPortalConnection.useBrowserAuth ||
        (!defaultPortalConnection.username &&
          !defaultPortalConnection.password))
    ) {
      dispatch(
        connectionAsyncActions.requestServerStatus({
          connection: defaultPortalConnection,
          params: { databases: false },
        })
      );
      dispatch(
        connectionAsyncActions.requestServerProperties({
          connection: defaultPortalConnection,
          params: ['catalog.database'],
        })
      );
      dataSourcesStardogRequestDispatchers.requestDataSourceTypeDescription(
        defaultPortalConnection
      );
    }
  }, [didDefaultConnectionChange, defaultPortalConnection, dispatch]);

  React.useEffect(() => {
    if (didLocationChange) {
      dispatch(
        uiSlice.actions.changedLocation({ route: location.pathname as ROUTE })
      );
    }
  }, [dispatch, didLocationChange, location]);

  return null;
};
